import React, { useEffect, useMemo, useState } from 'react';
import { freeze } from '@reduxjs/toolkit';
import Sankey from './Sankey';
import SankeyLinkTooltip from './SankeyLinkTooltip';
import { linkSort, nodeSort } from './utils';
import {
  pathSetMaxSeqAction,
  setChartSettingPathNodeCustomizationAction,
  setChartSettingPathObservedNodeIdAction,
} from '../../../redux/slices/analyses';
import {
  ILogicalOperator,
  LogicalOperatorKey,
  PathModeKey,
  QueryType,
} from '../../../utils/apollo/graphqlSchema';
import {
  IDimensions,
  IPathNodeCustomization,
  NodeCustomizationScope,
} from './interfaces';
import { assertCondition, assertNotNil } from '../../../utils/types';
import { useChartContext } from '../ChartContext';
import { IBreakdownOverallStatus } from '../../../utils/interfaces';
import { useSankeyState } from './useSankeyState';
import SankeyFactory from './D3Sankey/sankey';
import { NODE_TOOLTIP_HEIGHT, NODE_WIDTH } from './constants';
import SankeyNodeContextMenu from './SankeyNodeContextMenu';
import { isNil } from 'lodash';
import {
  getLargestPrevStepEventsToStart,
  observeNode,
  transformData,
} from './sankeyChartUtils';
import { setCachedFunnel } from '../../../redux/slices/cachedEntities';
import { createFunnelFromPath } from '../../Builder/utils/builderUtils';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../redux/hooks';
import { getQueryTypePagePath } from '../../../utils';
import { IStateHint } from '../../../utils/stateHint';
import SankeyChartWrapper from './SankeyChartWrapper';
import { ChartMode } from '../types';
import useCacheSankeyLlmRawDataEffect from '../hooks/llmCacheRawData/useCacheSankeyLlmRawDataEffect';
import useFloatingContextMenu from '../../../utils/hooks/useFloatingContextMenu';
import { FloatingLayer } from '../../FloatingManager';
import useTooltipScrollCancellation from '../hooks/tooltip/useTooltipScrollCancellation';

interface IProps {
  analysisId: string;
  mode: ChartMode;
}

// When creating Funnel from Path we use AND operators
const andLogicOp = freeze<ILogicalOperator>(
  {
    __typename: 'LogicalOperator',
    key: LogicalOperatorKey.And,
    name: 'and',
  },
  true,
);

export default React.memo(({ analysisId, mode }: IProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    chartData,
    settings: { chartSettings },
    metadata: { isBreakdownOverall, query, datePicker, startDate, endDate },
  } = useChartContext();
  assertCondition(
    chartData.queryType === QueryType.Path &&
      chartData.mode === PathModeKey.Sankey,
  );

  assertCondition(query?.__typename === 'PathQuery');

  const {
    displaySeq,
    displayedSliceSettings,
    pathEventsPerStep,
    pathNodeSettings,
    pathObservedNodeId,
  } = chartSettings;
  const { slices: flows, maxSeq, canCreateFunnel } = chartData;
  const [tooltipLinkIndex, setTooltipLinkIndex] = useState(-1);

  const {
    isOpen: isSankeyNodeMenuOpen,
    handleOpen: openSankeyNodeMenu,
    handleClose: closeSankeyNodeMenu,
    setFloating,
    floatingStyles,
    getFloatingProps,
  } = useFloatingContextMenu();

  const [customizationScope, setCustomizationScope] =
    useState<NodeCustomizationScope>(NodeCustomizationScope.IN_THIS_STEP);

  const isBase = isBreakdownOverall === IBreakdownOverallStatus.NoBreakdown;

  const [mousePosition, setMousePosition] = useState({
    left: 0,
    top: 0,
  });

  const [isLockObservedNode, setIsLockObservedNode] =
    useState(!!pathObservedNodeId);

  const { sankey, sankeyUpdate, sankeyReplace } = useSankeyState();

  const observedNodeIndex = useMemo(() => {
    const index = sankey.nodes.findIndex((n) => n.id === pathObservedNodeId);
    return index >= 0 ? index : null;
  }, [pathObservedNodeId, sankey.nodes]);

  const isObserveMode = !isNil(observedNodeIndex);

  const isThereAPathFromObservedNodeToAStartNode = useMemo(() => {
    if (isNil(observedNodeIndex)) {
      return false;
    }
    let currentNode = sankey.nodes[observedNodeIndex];
    const maxIterations = 100; // avoid endless search in case of wrong graph that never reaches the start
    for (let i = 0; i < maxIterations; i++) {
      const visibleLink = currentNode.targetLinks.find((link) => link.visible);
      if (isNil(visibleLink)) {
        break;
      }
      currentNode = visibleLink.source;
    }
    return currentNode.seq === 0;
  }, [observedNodeIndex, sankey.nodes]);

  const canCreateFunnelFromObservedNode = useMemo((): IStateHint => {
    if (
      canCreateFunnel.isDisabled ||
      isThereAPathFromObservedNodeToAStartNode
    ) {
      return canCreateFunnel;
    }

    return {
      isDisabled: true,
      hint:
        "You can't create funnel from this path because it doesn't reach " +
        'the beginning. Split steps from "Others" in previous steps and try again.',
    };
  }, [canCreateFunnel, isThereAPathFromObservedNodeToAStartNode]);

  const [chartDimensions, setChartDimensions] = useState<IDimensions>({
    width: 0,
    height: 0,
  });

  // Initialize and remap the nodes and path
  useEffect(() => {
    // If we need to debug real data from other environments like PROD,
    // we can parse json object and using chart data here.
    const { pathNodes, pathLinks, maxSeq } = transformData(
      flows,
      displayedSliceSettings,
      displaySeq,
      pathEventsPerStep,
      pathNodeSettings,
    );

    dispatch(
      pathSetMaxSeqAction({
        id: analysisId,
        pathProps: {
          maxSeq,
        },
      }),
    );
    if (pathNodes.length > 0) {
      const sankey = SankeyFactory();
      sankey.nodeWidth(NODE_WIDTH);
      sankey.nodeSort(nodeSort);
      sankey.linkSort(linkSort);
      const nodePadding = Math.round(NODE_TOOLTIP_HEIGHT * 1.2);
      sankey.nodePadding(nodePadding);
      sankey.extent([
        [NODE_WIDTH, nodePadding],
        [0, 0],
      ]);

      sankey.nodes(pathNodes);
      sankey.links(pathLinks);
      const graph = sankey();
      sankeyReplace({
        nodes: graph.nodes,
        links: graph.links,
      });
      setChartDimensions(graph.size);
    } else {
      sankeyReplace({
        nodes: [],
        links: [],
      });
    }
  }, [
    analysisId,
    dispatch,
    displaySeq,
    displayedSliceSettings,
    pathEventsPerStep,
    flows,
    isBreakdownOverall,
    sankeyReplace,
    pathNodeSettings,
  ]);

  useEffect(() => {
    const removeLinkIndex = () => {
      if (tooltipLinkIndex >= 0) {
        setTooltipLinkIndex(-1);
      }
    };
    window.addEventListener('wheel', removeLinkIndex);
    return () => window.removeEventListener('wheel', removeLinkIndex);
  }, [tooltipLinkIndex]);

  useTooltipScrollCancellation({
    closeTooltip: () => {
      closeSankeyNodeMenu();
    },
  });

  const onNodeMouseOver = (nodeIndex: number) => {
    if (isLockObservedNode || isSankeyNodeMenuOpen) {
      return;
    }
    dispatch(
      setChartSettingPathObservedNodeIdAction({
        analysisId,
        observedNodeId: sankey.nodes[nodeIndex].id,
      }),
    );
    sankeyUpdate(({ nodes }) => {
      nodes[nodeIndex].textVisible = true;
    });
  };

  const onNodeMouseLeave = (nodeIndex: number) => {
    // for the menu position check: when open menu, the mouseleave would be triggered by mouse over the menu
    if (isLockObservedNode || isSankeyNodeMenuOpen) {
      return;
    }
    dispatch(
      setChartSettingPathObservedNodeIdAction({
        analysisId,
        observedNodeId: null,
      }),
    );
    sankeyUpdate(({ nodes }) => {
      nodes[nodeIndex].textVisible = false;
    });
  };

  const onResetVisibleStatus = () => {
    setTooltipLinkIndex(-1);
    setIsLockObservedNode(false);
    sankeyUpdate(({ links, nodes }) => {
      links.forEach((link) => {
        link.visible = true;
      });
      nodes.forEach((node) => {
        node.visible = true;
        node.textVisible = false;
      });
    });

    closeSankeyNodeMenu();

    dispatch(
      setChartSettingPathObservedNodeIdAction({
        analysisId,
        observedNodeId: null,
      }),
    );
  };

  const onLinkMouseLeave = (linkIndex?: number) => {
    sankeyUpdate(({ links }) => {
      if (
        isObserveMode &&
        linkIndex &&
        linkIndex >= 0 &&
        (!links[linkIndex].source.visible || !links[linkIndex].target.visible)
      ) {
        return;
      }
      if (isObserveMode) {
        setTooltipLinkIndex(-1);
        return;
      }

      links.forEach((link) => {
        link.visible = true;
        link.isHighlight = false;
      });
      dispatch(
        setChartSettingPathObservedNodeIdAction({
          analysisId,
          observedNodeId: null,
        }),
      );
      setTooltipLinkIndex(-1);
    });
  };

  const onLinkMouseOver = (
    event: React.MouseEvent<SVGElement, MouseEvent>,
    linkIndex: number,
  ) => {
    sankeyUpdate(({ links }) => {
      if (
        isObserveMode &&
        (!links[linkIndex].source.visible || !links[linkIndex].target.visible)
      ) {
        return;
      }

      const newMousePosition = {
        left: event.clientX + 2,
        top: event.clientY + 2,
      };

      setMousePosition(newMousePosition);

      links.forEach((link, index) => {
        link.isHighlight = index === linkIndex;
      });

      setTooltipLinkIndex(linkIndex);
    });
  };

  const onNodeClick = (event: React.MouseEvent, nodeIndex: number) => {
    // prevent the click for onResetVisibleStatus
    event.stopPropagation();
    dispatch(
      setChartSettingPathObservedNodeIdAction({
        analysisId,
        observedNodeId: sankey.nodes[nodeIndex].id,
      }),
    );

    openSankeyNodeMenu(event);
  };

  const onLinkMouseMove = (event: React.MouseEvent<SVGElement, MouseEvent>) => {
    const newMousePosition = {
      left: event.clientX + 2,
      top: event.clientY + 2,
    };
    const yChange = Math.abs(newMousePosition.top - mousePosition.top);
    const xChange = Math.abs(newMousePosition.left - mousePosition.left);
    if (yChange > 40 || xChange > 40) {
      setMousePosition(newMousePosition);
    }
  };

  useEffect(() => {
    sankeyUpdate(({ nodes, links }) => {
      if (isObserveMode) {
        observeNode(nodes, links, observedNodeIndex);
      } else {
        nodes.forEach((node) => (node.visible = true));
        links.forEach((link) => (link.visible = true));
      }
    });
  }, [isObserveMode, observedNodeIndex, sankeyUpdate]);

  const createFunnel = () => {
    assertNotNil(observedNodeIndex);

    const currentStep = sankey.nodes[observedNodeIndex];

    const { groupAlias, name, groupHint } = currentStep;
    const currentStepEvents = [name];

    const stepEvents = [
      ...getLargestPrevStepEventsToStart(currentStep),
      currentStepEvents,
    ];

    dispatch(
      setCachedFunnel(
        createFunnelFromPath(
          stepEvents,
          groupAlias,
          groupHint,
          query,
          {
            start: startDate,
            end: endDate,
            datePicker,
          },
          andLogicOp,
        ),
      ),
    );

    navigate(getQueryTypePagePath(QueryType.Funnel));
  };

  const nodeCustomizationHandler = (
    nodeCustomization: IPathNodeCustomization,
  ) => {
    dispatch(
      setChartSettingPathNodeCustomizationAction({
        analysisId,
        pathNodeCustomization: nodeCustomization,
        pathBreadth: maxSeq,
      }),
    );
    dispatch(
      setChartSettingPathObservedNodeIdAction({
        analysisId,
        observedNodeId: null,
      }),
    );
  };

  const handleLockObservedNode = () => {
    setIsLockObservedNode(true);

    closeSankeyNodeMenu();
  };

  // XXX also cache the raw chart numbers in redux, to be used as a context sent to the LLM
  useCacheSankeyLlmRawDataEffect({ analysisId, sankey });

  return (
    <SankeyChartWrapper analysisId={analysisId} mode={mode}>
      <Sankey
        nodes={sankey.nodes}
        links={sankey.links}
        dimensions={chartDimensions}
        isBase={isBase}
        isObserveMode={isObserveMode}
        onNodeMouseOver={onNodeMouseOver}
        onNodeMouseLeave={onNodeMouseLeave}
        onResetVisibleStatus={onResetVisibleStatus}
        onLinkMouseLeave={onLinkMouseLeave}
        onLinkMouseMove={onLinkMouseMove}
        onLinkMouseOver={onLinkMouseOver}
        onNodeClick={onNodeClick}
        mode={mode}
      />

      <SankeyLinkTooltip
        analysisId={analysisId}
        isBase={isBase}
        isObserveMode={isObserveMode}
        left={mousePosition.left}
        top={mousePosition.top}
        links={sankey.links}
        tooltipLinkIndex={tooltipLinkIndex}
      />
      {isSankeyNodeMenuOpen && (
        <FloatingLayer>
          <div ref={setFloating} style={floatingStyles} {...getFloatingProps()}>
            <SankeyNodeContextMenu
              customizationScope={customizationScope}
              setCustomizationScope={setCustomizationScope}
              onCreateFunnel={createFunnel}
              canCreateFunnel={canCreateFunnelFromObservedNode}
              nodes={sankey.nodes}
              observedNodeIndex={observedNodeIndex}
              onNodeCustomization={nodeCustomizationHandler}
              onLockObservedNode={handleLockObservedNode}
            />
          </div>
        </FloatingLayer>
      )}
    </SankeyChartWrapper>
  );
});
